import { createRouter, createWebHistory } from "vue-router";

import Home from "@/views/Home.vue";
import NotFound from "@/views/NotFound.vue";
import Login from "@/views/auth/Login.vue";
import { isFeatureEnabled } from "@/config/featureFlags.js";

import salesRoutes from "./sales.js"
import reviewsRoutes from "./reviews.js"
import assetsRoutes from "./assets.js"
import campaignsRoutes from "./campaigns.js"
import contactsRoutes from "./contacts.js"
import hrRoutes from "./hr.js"
import pettycashRoutes from "./pettycash.js"
import menuRoutes from "./menu.js"
import warehouseRoutes from "./warehouse.js"
import disputesRoutes from "./disputes.js"
import journalRoutes from "./journal.js"
import tasksRoutes from "./tasks.js"

const routes = [
  // ADD THESE ROUTES BACK:
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: () => import("@/views/auth/AccessDenied.vue"),
    meta: { public: true },
  },
  {
    path: "/no-access",
    name: "NoAccess",
    component: () => import("@/views/auth/NoAccess.vue"),
    meta: {
      public: true,
      standalone: true, // Add this flag
    },
  },
  {
    path: "/",
    name: "Root",
    component: Login,
    meta: { public: true },
  },
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { public: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("@/views/auth/ForgotPassword.vue"),
    meta: { public: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("@/views/auth/ResetPassword.vue"),
    meta: { public: true },
  },
  {
    path: "/activate-account",
    name: "ActivateAccount",
    component: () => import("@/views/auth/ActivateAccount.vue"),
    meta: { public: true },
  },
  {
    path: "/force-password-change",
    name: "ForcePasswordChange",
    component: () => import("@/views/auth/ForcePasswordChange.vue"),
    meta: { requiresAuth: true, skipPasswordCheck: true },
  },
  {
    path: "/home",
    name: "Home",
    component: Home,
    meta: { requiresAuth: true },
  },
  {
    path: "/accounting",
    name: "AccountingHome",
    component: () => import("@/views/accounting/AccountingHome.vue"),
    meta: { requiresAuth: true, app: ["accounting", "sales", "pettycash", "warehouse"] },
  },
  {
    path: "/accounting/delivery",
    name: "Delivery",
    component: () => import("@/views/accounting/DeliveryPage.vue"),
    meta: { requiresAuth: true, app: "warehouse" },
  },
  {
    path: "/accounting/items",
    name: "Items",
    component: () => import("@/views/accounting/ItemsPage.vue"),
    meta: { requiresAuth: true, app: "warehouse" },
  },
  {
    path: "/accounting/costs-gl",
    name: "CostsGL",
    // GL features live behind the dedicated "journal" app so they stay dark until
    // the GL suite is released (was the shared "accounting" app, which leaked to
    // accounting users). Grant "journal" to release the whole GL suite at once.
    component: () => import("@/views/accounting/CostsGLPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "journal" },
  },
  {
    path: "/accounting/settlements",
    name: "Settlements",
    component: () => import("@/views/accounting/SettlementsPage.vue"),
    meta: { requiresAuth: true, requiresAdmin: true, app: "journal" },
  },
  {
    path: "/marketing",
    name: "MarketingHome",
    component: () => import("@/views/marketing/MarketingHome.vue"),
    // TEMP: marketing service retired to appadmins only
    meta: { requiresAuth: true, requiresAppAdmin: true, app: ["reviews", "campaigns"] },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () => import("@/views/settings/SettingsHome.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/account",
    name: "Account",
    component: () => import("@/views/account/AccountHome.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings/users",
    name: "UsersManagement",
    component: () => import("@/views/settings/UsersManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/audit-logs",
    name: "AuditLogs",
    component: () => import("@/views/settings/AuditLogs.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/restaurant-mappings",
    name: "RestaurantMappings",
    component: () => import("@/views/settings/RestaurantMappings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  // {
  //   path: "/settings/platform-mappings",
  //   name: "PlatformColumnMappings",
  //   component: () => import("@/views/settings/PlatformColumnMappings.vue"),
  //   meta: { requiresAuth: true, requiresAppAdmin: true },
  // },
  // {
  //   path: "/settings/platform-credentials",
  //   name: "PlatformCredentials",
  //   component: () => import("@/views/settings/PlatformCredentials.vue"),
  //   meta: { requiresAuth: true, requiresAppAdmin: true },
  // },
  {
    path: "/settings/business-rules",
    name: "BusinessRules",
    component: () => import("@/views/settings/BusinessRules.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/options",
    name: "OptionsManagement",
    component: () => import("@/views/settings/OptionsManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/platform-credentials",
    name: "PlatformCredentials",
    component: () => import("@/views/settings/PlatformCredentials.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/assets",
    name: "AssetSettings",
    component: () => import("@/views/settings/AssetSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/tags",
    name: "TagsManagement",
    component: () => import("@/views/settings/TagsManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/tasks",
    name: "TasksSettings",
    component: () => import("@/views/settings/TasksSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/account-managers",
    name: "AccountManagersManagement",
    component: () => import("@/views/settings/AccountManagersManagement.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/address-book",
    name: "AddressBookSettings",
    component: () => import("@/views/settings/AddressBookSettings.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  {
    path: "/settings/reviews-admin",
    name: "ReviewsAdmin",
    component: () => import("@/views/reviews/ReviewsAdmin.vue"),
    // TEMP: marketing service retired to appadmins only
    meta: { requiresAuth: true, requiresAppAdmin: true, app: "reviews" },
  },
  // API Interface route removed — replaced by Export API JSON on Documentation page
  {
    path: "/settings/docs",
    name: "Documentation",
    component: () => import("@/views/settings/DocumentationPage.vue"),
    meta: { requiresAuth: true, requiresAppAdmin: true },
  },
  // 404 - Catch-all route - MUST BE LAST
  {
    path: "/:catchAll(.*)*",
    name: "NotFound",
    component: NotFound,
    meta: { public: true },
  },
];

// TEMP: the marketing service (campaigns, reviews, disputes) is retired to
// appadmins only. Revert by removing appAdminOnly() below + the requiresAppAdmin
// flags on MarketingHome / ReviewsAdmin above.
const appAdminOnly = (rs) => rs.map((r) => ({ ...r, meta: { ...r.meta, requiresAppAdmin: true } }));

const router = createRouter({
  history: createWebHistory(),
  routes: routes.concat(salesRoutes, appAdminOnly(reviewsRoutes), assetsRoutes, appAdminOnly(campaignsRoutes), contactsRoutes, hrRoutes, pettycashRoutes, menuRoutes, warehouseRoutes, appAdminOnly(disputesRoutes), journalRoutes, tasksRoutes),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

// Navigation guard for authentication and app access
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("authToken");
  const userStr = localStorage.getItem("user");
  const passwordChangeRequired = localStorage.getItem("passwordChangeRequired") === "true";
  let user = {};
  let allowedApps = [];

  try {
    user = userStr ? JSON.parse(userStr) : {};
    const apps = user.allowed_apps;
    if (Array.isArray(apps)) {
      allowedApps = apps;
    } else if (apps) {
      allowedApps = JSON.parse(apps);
    }
  } catch (e) {
    console.error("Error parsing user:", e);
  }

  // If authenticated user visits Login page, redirect to appropriate home
  if ((to.name === "Login" || to.name === "Root") && token && allowedApps.length > 0) {
    // Check if password change is required first
    if (passwordChangeRequired) {
      next({ name: "ForcePasswordChange" });
      return;
    }
    // Resume at the last visited page only when the user's preference allows it.
    // The setting lives in user_preferences (server) and is mirrored to
    // localStorage as "pref_resumeLastPage" by useUserPreferences — read that
    // mirror synchronously here (the guard runs before async prefs load).
    // Default true preserves the prior always-resume behavior; the Account page
    // exposes a toggle to land on Home instead, plus a Reset-to-Home button.
    let resumeLastPage = true;
    try {
      const raw = localStorage.getItem("pref_resumeLastPage");
      if (raw !== null) resumeLastPage = JSON.parse(raw) === true;
    } catch { /* malformed — keep default */ }

    const lastPath = localStorage.getItem("lastPath");
    if (resumeLastPage && lastPath && !["/login", "/", "/home"].includes(lastPath)) {
      next({ path: lastPath });
    } else {
      next({ path: "/home" });
    }
    return;
  }

  // Skip auth check for public routes
  if (to.meta.public) {
    // console.log("Skipping auth check for public route:", to.name);
    next();
    return;
  }

  // Feature-flagged routes: if the feature is off, behave as if the page doesn't
  // exist (lets us deploy code dark and flip it on without a rebuild).
  if (to.meta.feature && !isFeatureEnabled(to.meta.feature)) {
    next({ name: "Home" });
    return;
  }

  // Route requires authentication but user is not authenticated
  if (to.meta.requiresAuth && !token) {
    if (to.path && to.path !== "/login" && to.path !== "/") {
      localStorage.setItem("lastPath", to.path);
    }
    next({ name: "Login" });
    return;
  }

  // User is authenticated - check access
  if (token) {
    // Check if password change is required (unless on the ForcePasswordChange page or it has skipPasswordCheck)
    if (passwordChangeRequired && !to.meta.skipPasswordCheck && to.name !== "ForcePasswordChange") {
      next({ name: "ForcePasswordChange" });
      return;
    }

    // appadmin has full access to everything
    if (user.role === "appadmin") {
      // Only check requiresAppAdmin routes - appadmin can access them
      // For all other routes, appadmin has full access
      next();
      return;
    }

    // Check app admin access (non-appadmin users denied)
    if (to.meta.requiresAppAdmin) {
      next({ name: "AccessDenied", query: { reason: "appadmin" } });
      return;
    }

    // Check admin access (admin or appadmin roles)
    if (to.meta.requiresAdmin && !["admin", "appadmin"].includes(user.role)) {
      next({ name: "AccessDenied", query: { reason: "admin" } });
      return;
    }

    // For regular users, check if they have access to ANY app
    if (allowedApps.length === 0) {
      if (to.name !== "NoAccess") {
        next({ name: "NoAccess" });
        return;
      }
    }

    // Check specific app access if route has app requirement
    // meta.app can be a string or an array (user needs at least one match)
    const requiredApp = to.meta.app;
    if (requiredApp && (
      Array.isArray(requiredApp)
        ? !requiredApp.some((a) => allowedApps.includes(a))
        : !allowedApps.includes(requiredApp)
    )) {
      // Show the first required app name (for display purposes)
      const appLabel = Array.isArray(requiredApp) ? requiredApp[0] : requiredApp;
      next({ name: "AccessDenied", query: { reason: "app", app: appLabel } });
      return;
    }
  }

  // console.log("✅ All checks passed, proceeding to route");
  next();
});

// Save last visited authenticated page (for post-login redirect after tab close)
router.afterEach((to) => {
  if (to.meta.requiresAuth && !to.meta.skipPasswordCheck) {
    localStorage.setItem("lastPath", to.path);
  }
});

export default router;
